import type { TrVertragsart } from "./types";

/**
 * Abschnitts- und Feldstruktur der Transport-Vertragsmaske.
 *
 * Abgebildet ist das Blatt „Vertragsmaske-Transport" der Felddefinition
 * „Transport Detailanalyse + Vertragsmaske.xlsx" (Stand 28.08.2026) mit seinen
 * fünf Vertragsarten:
 *
 *   2. Warentransportversicherung
 *   3. Fahrzeuginhaltsversicherung (Werkverkehr)
 *   4. Frachtführerhaftpflicht
 *   5. Messeversicherung
 *   6. Reisegepäckversicherung
 *
 * Jede Vertragsart behält die feste Blockfolge der Felddefinition:
 * A Wichtigste Vertragsdaten → B Deckungsdetails → C Bedingungen / Klauseln /
 * Einschränkungen → D Informationen aus der Analyse. Abschnitt 1
 * „Vertragsübersicht" ist keine eigene Feldliste, sondern die Startseite: sie
 * zeigt je Vertragsart die Eckdaten aus `uebersicht`.
 *
 * Zeilenkataloge, die die Felddefinition an anderer Stelle führt, sind bei den
 * Untertabellen hinterlegt: Warengruppen (TR-D04), Transportarten (TR-D57),
 * Abstellarten (TR-D35), Sachenarten Reisegepäck (RG-V03). Einzige Ausnahme
 * ist der Gefahrenkatalog zu WA-V20 — die Felddefinition nennt dort keine
 * Zeilen, deshalb steht hier eine transportübliche Liste, die sich ergänzen
 * lässt.
 *
 * Ohne Inhalt geblieben sind die in der Excel leeren Zeilen Ü-V05, WA-A02,
 * WA-A03, WV-V09, WV-V10 und FF-V04: dort steht nur eine Nummer, kein Feld.
 */
export const TRANSPORT_VERTRAGSARTEN: TrVertragsart[] = [
  // ── 2. Warentransportversicherung ────────────────────────────────
  {
    key: "warentransport",
    nr: 2,
    title: "Warentransportversicherung",
    shortTitle: "Warentransport",
    color: "blue",
    intro:
      "Zuerst die wichtigsten Vertragsdaten, danach Deckungsdetails, dann Bedingungen und Klauseln — zuletzt die wesentlichen Analyseinformationen.",
    uebersicht: [
      { label: "Versicherungssumme", fieldKey: "wa_v09" },
      { label: "Geltungsbereich", fieldKey: "wa_v07" },
      { label: "Selbstbehalt", fieldKey: "wa_v13" },
      { label: "Policenform", fieldKey: "wa_v01" },
    ],
    blocks: [
      {
        letter: "A",
        key: "warentransport_a",
        title: "Wichtigste Vertragsdaten",
        fields: [
          {
            id: "WA-V01",
            key: "wa_v01",
            label: "Policenform",
            type: "single",
            options: [
              "Einzelpolice",
              "Generalpolice",
              "laufende Police",
              "Rahmenvertrag",
            ],
            source: "POLART",
            hint: "Grundstruktur des Warentransportvertrags.",
          },
          {
            id: "WA-V09",
            key: "wa_v09",
            label: "Versicherungssumme",
            type: "eur",
            placeholder: "0,00",
            source: "VSUM",
            hint: "Versicherungssumme der Warentransportdeckung.",
          },
          {
            id: "WA-V10",
            key: "wa_v10",
            label: "Transportmaxima",
            feld: "Maxima je Transport",
            type: "subtable",
            subtable: {
              rowLabel: "Art des Maximums",
              rows: [
                "Maximum je Versendung",
                "Maximum je Bezug",
                "Maximum je Sendung",
                "Maximum je Transportmittel",
              ],
              addable: true,
              columns: [
                { key: "betrag", label: "Betrag (EUR)", type: "eur", width: "180px" },
                { key: "bemerkung", label: "Bemerkung", type: "text" },
              ],
            },
            source: "TR-V20",
            hint: "Entscheidend für die Unterdeckungsprüfung.",
          },
          {
            id: "WA-V11",
            key: "wa_v11",
            label: "Weitere Maxima / Sublimits",
            type: "subtable",
            subtable: {
              rowLabel: "Art des Maximums",
              rows: [
                "Kumulmaximum",
                "Jahreshöchstentschädigung",
                "Sublimit",
              ],
              addable: true,
              columns: [
                { key: "betrag", label: "Betrag (EUR)", type: "eur", width: "180px" },
                { key: "bemerkung", label: "Bemerkung", type: "text" },
              ],
            },
            source: "TR-V22",
            hint: "Nur tatsächlich vereinbarte Maxima/Sublimits führen.",
          },
          {
            id: "WA-V12",
            key: "wa_v12",
            label: "Versicherungswert",
            feld: "Versicherungswert / Wertbasis",
            type: "single",
            options: [
              "Einkaufs-/Fakturawert",
              "Wiederbeschaffungswert",
              "Herstellkosten",
              "Verkaufspreis",
              "Neuwert",
              "Zeitwert",
              "Taxwert / feste Taxe",
              "Marktpreis",
              "sonstige",
            ],
            source: "VWERT",
            hint: "Bewertungsgrundlage im Schadenfall.",
          },
          {
            id: "WA-V13",
            key: "wa_v13",
            label: "Selbstbehalt",
            type: "combo",
            parts: [
              { key: "art", label: "Art", type: "select", options: [
                "fester Selbstbehalt in EUR",
                "prozentualer Selbstbehalt in %",
                "kombiniert",
                "kein Selbstbehalt",
              ] },
              { key: "eur", label: "Betrag (EUR)", type: "eur" },
              { key: "prozent", label: "Anteil (%)", type: "percent" },
            ],
            source: "SBFIX / SBPROZ",
            hint: "Standard-Selbstbehalt der Warentransportdeckung.",
          },
          {
            id: "WA-V07",
            key: "wa_v07",
            label: "Geltungsbereich",
            type: "single",
            options: [
              "Deutschland",
              "geografisches Europa",
              "weltweit",
              "individuell / gemäß Vereinbarung",
            ],
            source: "GELTBER",
            hint: "Vertraglichen Geltungsbereich mit der Analyse vergleichen.",
          },
          {
            id: "WA-V06",
            key: "wa_v06",
            label: "Deckungsform",
            type: "single",
            options: [
              "volle Deckung / Allgefahren",
              "eingeschränkte Deckung / benannte Gefahren",
              "individuelle Deckungsform",
            ],
            source: "TR-V17",
            hint: "Zentrale Information zum Deckungsumfang.",
          },
        ],
      },
      {
        letter: "B",
        key: "warentransport_b",
        title: "Deckungsdetails",
        fields: [
          {
            id: "WA-V02",
            key: "wa_v02",
            label: "Versicherte Transportrichtungen",
            type: "multi",
            options: [
              "Versendungen",
              "Bezüge",
              "Transporte zwischen eigenen Standorten",
              "Baustellentransporte",
              "Rücktransporte / Retouren",
            ],
            source: "TR-V03",
            hint: "Nur die tatsächlich versicherten Transportrichtungen anzeigen.",
          },
          {
            id: "WA-V03",
            key: "wa_v03",
            label: "Versicherungsinteresse",
            feld: "Versichertes Interesse / Gefahrtragung",
            type: "single",
            options: [
              "eigene Sachen / eigene Gefahrtragung",
              "fremde Sachen / fremdes Interesse mitversichert",
              "alle vereinbarten Transporte unabhängig von der Gefahrtragung",
              "individuelle Regelung",
            ],
            source: "TR-V35",
            hint: "Wichtig für die Frage, wann der Versicherungsschutz greift.",
          },
          {
            id: "WA-V04",
            key: "wa_v04",
            label: "Versicherte Güter",
            feld: "Versicherte Güter / Warengruppen",
            type: "subtable",
            subtable: {
              rowLabel: "Warengruppe",
              rows: [
                "Handelswaren",
                "Roh-/Hilfs-/Betriebsstoffe",
                "Halbfertigwaren",
                "Fertigwaren",
                "Maschinen/Geräte",
                "Werkzeuge",
                "Ersatzteile",
                "Baustoffe/Bauteile",
                "Kundeneigentum",
                "Muster/Modelle",
                "Messe-/Ausstellungsgut",
                "sonstige",
              ],
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "versichert",
                  "eingeschränkt",
                  "ausgeschlossen",
                ], width: "170px" },
                { key: "sublimit", label: "Sublimit (EUR)", type: "eur", width: "160px" },
                { key: "regelung", label: "Besondere Regelung", type: "text" },
              ],
            },
            source: "TR-V04",
            hint: "Welche Güter sind vom Vertrag tatsächlich umfasst?",
          },
          {
            id: "WA-V05",
            key: "wa_v05",
            label: "Versicherte Transportmittel",
            type: "multi",
            options: [
              "fremde Frachtführer / Spedition",
              "Paket-/Kurierdienst",
              "Bahn",
              "Binnenschiff",
              "Seeschiff",
              "Luftfracht",
              "sonstige",
            ],
            source: "TR-V06",
            hint: "Eigene Fahrzeuge werden im Abschnitt Werkverkehr behandelt.",
          },
          {
            id: "WA-V15",
            key: "wa_v15",
            label: "Beginn Versicherungsschutz",
            feld: "Beginn der Gefahrzeit",
            type: "single",
            options: [
              "Verlassen des Ausgangslagers",
              "Beginn der Verladung",
              "Übergabe an Frachtführer/Spediteur",
              "individuelle Regelung",
            ],
            source: "TR-V09",
            hint: "Wann beginnt der versicherte Transport?",
          },
          {
            id: "WA-V16",
            key: "wa_v16",
            label: "Ende Versicherungsschutz",
            feld: "Ende der Gefahrzeit",
            type: "single",
            options: [
              "Ablieferung beim Empfänger",
              "Ende der Entladung",
              "Eingang im Bestimmungslager",
              "individuelle Regelung",
            ],
            source: "TR-V10",
            hint: "Wann endet der versicherte Transport?",
          },
          {
            id: "WA-V17",
            key: "wa_v17",
            label: "Erweiterte Gefahrzeit",
            type: "multi",
            options: [
              "Haus-zu-Haus",
              "Lager-zu-Lager",
              "Rücktransporte",
              "Retouren",
              "Transportunterbrechungen / Aufenthalte",
              "sonstige",
            ],
            source: "TR-V11",
            hint: "Nur vereinbarte Erweiterungen anzeigen.",
          },
          {
            id: "WA-V18",
            key: "wa_v18",
            label: "Be-/Entladung / Umschlag",
            feld: "Be-/Entladung und Umschlag",
            type: "subtable",
            subtable: {
              rowLabel: "Bereich",
              rows: [
                "Beladung",
                "Entladung",
                "Umschlag",
              ],
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "versichert",
                  "eingeschränkt",
                  "ausgeschlossen",
                ], width: "170px" },
                { key: "limit", label: "Limit (EUR)", type: "eur", width: "160px" },
                { key: "sb", label: "Selbstbehalt", type: "text", width: "140px" },
              ],
            },
            source: "TR-V12",
            hint: "Nur relevant, wenn diese Tätigkeiten mitversichert sind.",
          },
          {
            id: "WA-V19",
            key: "wa_v19",
            label: "Zwischenlagerungsdeckung",
            feld: "Transportbedingte Zwischenlagerung",
            type: "subtable",
            subtable: {
              rowLabel: "Regelung",
              rows: [
                "Zwischenlagerung transportbedingt",
              ],
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "versichert",
                  "eingeschränkt",
                  "ausgeschlossen",
                ], width: "170px" },
                { key: "dauer", label: "Maximale Dauer", type: "duration", width: "170px" },
                { key: "maximum", label: "Maximum (EUR)", type: "eur", width: "160px" },
                { key: "sicherung", label: "Sicherungsvorgaben", type: "text" },
              ],
            },
            source: "TR-V13–TR-V16",
            hint: "Mit den Analyseangaben zur Zwischenlagerung abgleichen.",
          },
          {
            id: "WA-V22",
            key: "wa_v22",
            label: "Deklaration / Meldung",
            feld: "Deklarations-/Meldeverfahren",
            type: "single",
            options: [
              "keine Deklaration",
              "laufende Deklaration",
              "Einzelanmeldung je Transport",
              "Monatsmeldung",
              "Quartalsmeldung",
              "Jahresmeldung",
              "sonstige",
            ],
            source: "TR-V31",
            hint: "Vor allem bei General- und laufenden Policen.",
          },
          {
            id: "WA-V23",
            key: "wa_v23",
            label: "Abrechnungsgrundlage",
            type: "single",
            options: [
              "Transportumsatz",
              "Warenumsatz",
              "Transportvolumen",
              "Einzelwerte / Einzeldeklaration",
              "pauschale Grundlage",
              "sonstige",
            ],
            source: "TR-V32",
            hint: "Mit den Jahreswerten der Analyse plausibilisieren.",
          },
          {
            id: "WA-V24",
            key: "wa_v24",
            label: "Mindestprämie / Mindestumsatz",
            feld: "Mindestbasis",
            type: "subtable",
            subtable: {
              rowLabel: "Mindestbasis",
              rows: [
                "Mindestprämie",
                "Mindestumsatz / -transportwert",
              ],
              columns: [
                { key: "betrag", label: "Betrag (EUR)", type: "eur", width: "180px" },
                { key: "periode", label: "Bezugsperiode", type: "text", width: "200px" },
              ],
            },
            source: "TR-V46",
            hint: "Nur anzeigen, wenn vertraglich vereinbart.",
          },
          {
            id: "WA-V25",
            key: "wa_v25",
            label: "Besondere Transportarten",
            type: "subtable",
            subtable: {
              rowLabel: "Transportart",
              rows: [
                "Lohnarbeiten",
                "Zwischentransporte zwischen Betriebsstätten",
                "Transporte von/zu Verpackungsbetrieben",
                "Transporte von/zu Lohnveredelungsbetrieben",
                "Reparatursendungen",
                "Investitionsgütertransporte",
                "Vorführ-/Leihgeräte",
                "Austausch-/Garantiesendungen",
                "Auswahl-/Mustersendungen",
                "sonstige",
              ],
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "versichert",
                  "eingeschränkt",
                  "ausgeschlossen",
                ], width: "170px" },
                { key: "limit", label: "Limit (EUR)", type: "eur", width: "160px" },
                { key: "sb", label: "Selbstbehalt", type: "text", width: "140px" },
              ],
            },
            source: "TR-V36",
            hint: "z. B. Reparatur-, Vorführ-, Garantie-/Mustertransporte.",
          },
          {
            id: "WA-V26",
            key: "wa_v26",
            label: "Maximum je KEP-Paket",
            feld: "Maximum KEP-Paket",
            type: "eur",
            placeholder: "0,00",
            source: "TR-V38",
            hint: "Nur bei Paket-/Kurierdiensten.",
          },
        ],
      },
      {
        letter: "C",
        key: "warentransport_c",
        title: "Bedingungen / Klauseln / Einschränkungen",
        fields: [
          {
            id: "WA-V14",
            key: "wa_v14",
            label: "Bedingungsgrundlage",
            type: "single",
            options: [
              "DTV-Güter",
              "ICC A",
              "ICC B",
              "ICC C",
              "Kombination / Ergänzung",
              "sonstiges Bedingungswerk",
            ],
            source: "TR-V34",
            hint: "Für die Deckungsprüfung wichtig.",
          },
          {
            id: "WA-V08",
            key: "wa_v08",
            label: "Länderausschlüsse / Einschränkungen",
            feld: "Länder-/Routenausschlüsse",
            type: "subtable",
            subtable: {
              rowLabel: "Land / Region",
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "eingeschlossen",
                  "eingeschränkt",
                  "ausgeschlossen",
                  "nur nach Bestätigung",
                ], width: "200px" },
                { key: "klausel", label: "Klausel / Bemerkung", type: "text" },
              ],
            },
            source: "TR-V08",
            hint: "Nur anzeigen, wenn es Abweichungen vom allgemeinen Geltungsbereich gibt.",
          },
          {
            id: "WA-V20",
            key: "wa_v20",
            label: "Gefahrenumfang",
            feld: "Gefahren / Ausschlüsse",
            type: "subtable",
            subtable: {
              rowLabel: "Gefahr",
              rows: [
                "Transportmittelunfall",
                "Diebstahl / Raub",
                "Beschädigung / Bruch",
                "Nässe / Feuchtigkeit",
                "Feuer / Explosion",
                "Elementarereignisse",
                "Verderb / Temperaturabweichung",
                "Verlust / Abhandenkommen",
                "Krieg",
                "Streik / Aufruhr",
              ],
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "versichert",
                  "eingeschränkt",
                  "ausgeschlossen",
                  "separat vereinbart",
                ], width: "185px" },
                { key: "limit", label: "Limit (EUR)", type: "eur", width: "160px" },
                { key: "sb", label: "Selbstbehalt", type: "text", width: "140px" },
              ],
            },
            source: "TR-V18",
            hint: "Gefahrenmatrix nur bei Bedarf öffnen.",
          },
          {
            id: "WA-V21",
            key: "wa_v21",
            label: "Verpackungsregelung",
            feld: "Verpackungsregelungen",
            type: "subtable",
            subtable: {
              rowLabel: "Verpackungsregelung",
              rows: [
                "transportgerechte Verpackung",
                "Spezialverpackung",
                "Manipulations-/Öffnungsschutz",
                "Korrosionsschutz",
                "Transportüberwachung",
              ],
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "vereinbart",
                  "eingeschränkt",
                  "ausgeschlossen",
                ], width: "180px" },
                { key: "vorgabe", label: "Vorgabe / Einschränkung", type: "text" },
              ],
            },
            source: "TR-V30",
            hint: "Nur besondere vertragliche Vorgaben führen.",
          },
          {
            id: "WA-V27",
            key: "wa_v27",
            label: "Dual-Use / Exportrestriktionen",
            type: "single",
            options: [
              "mitversichert / akzeptiert",
              "nur nach Einzelprüfung / Freigabe",
              "ausgeschlossen",
              "besondere Vereinbarung erforderlich",
            ],
            source: "TR-V40",
            hint: "Nur anzeigen, wenn die Analyse einen entsprechenden Hinweis enthält.",
          },
          {
            id: "WA-V28",
            key: "wa_v28",
            label: "Klauseln / Sondervereinbarungen",
            type: "subtable",
            subtable: {
              rowLabel: "Klausel",
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "eingeschlossen",
                  "ausgeschlossen",
                  "abweichend vereinbart",
                ], width: "200px" },
                { key: "limit", label: "Limit (EUR)", type: "eur", width: "150px" },
                { key: "sb", label: "Selbstbehalt", type: "text", width: "140px" },
                { key: "bemerkung", label: "Bemerkung", type: "text" },
              ],
            },
            source: "TR-V33",
            hint: "Nur vertragsrelevante Sondervereinbarungen.",
          },
        ],
      },
      {
        letter: "D",
        key: "warentransport_d",
        title: "Informationen aus der Analyse",
        description:
          "Werte aus der Transport-Analyse. Sie werden hier nur gezeigt — geändert werden sie in der Analyse.",
        fields: [
          {
            id: "WA-A01",
            key: "wa_a01",
            label: "Transportierte Güter",
            feld: "Transportierte Güter / Warengruppen",
            type: "readout",
            source: "TR-D04",
            hint: "Wichtigster Risikoüberblick: Welche Güter werden tatsächlich transportiert?",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "WA-A04",
            key: "wa_a04",
            label: "Tatsächlicher Geltungsbereich",
            type: "readout",
            source: "TR-D24",
            hint: "Mit dem vertraglichen Geltungsbereich vergleichen.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "WA-A05",
            key: "wa_a05",
            label: "Höchster Einzelstückwert",
            type: "readout",
            source: "TR-D07",
            hint: "Relevant für Sublimits und hochwertige Einzelstücke.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "WA-A06",
            key: "wa_a06",
            label: "Besondere Transportmerkmale",
            type: "readout",
            source: "TR-D38",
            hint: "Nur Warn-/Hinweisfeld; Detailinformationen bleiben in der Analyse.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
        ],
      },
    ],
  },
  // ── 3. Fahrzeuginhaltsversicherung (Werkverkehr) ─────────────────
  {
    key: "werkverkehr",
    nr: 3,
    title: "Fahrzeuginhaltsversicherung (Werkverkehr)",
    shortTitle: "Werkverkehr",
    color: "green",
    intro:
      "Kernwerte und Grunddeckung zuerst, Risikodetails danach, Klauseln vor den Analyseinformationen.",
    uebersicht: [
      { label: "Maximum je Fahrzeug", fieldKey: "wv_v05" },
      { label: "Geltungsbereich", fieldKey: "wv_v04" },
      { label: "Selbstbehalt", fieldKey: "wv_v07" },
      { label: "Deckungszeitraum", fieldKey: "wv_v03" },
    ],
    blocks: [
      {
        letter: "A",
        key: "werkverkehr_a",
        title: "Wichtigste Vertragsdaten",
        fields: [
          {
            id: "WV-V05",
            key: "wv_v05",
            label: "Maximum je Fahrzeug",
            feld: "Versicherungssumme / Maximum je Fahrzeug",
            type: "eur",
            placeholder: "0,00",
            source: "TR-V20 / VSUM",
            hint: "Wichtigster Summenwert der Werkverkehrsdeckung.",
          },
          {
            id: "WV-V06",
            key: "wv_v06",
            label: "Versicherungswert Werkverkehr",
            feld: "Versicherungswert / Wertbasis",
            type: "single",
            options: [
              "Neuwert",
              "Zeitwert",
              "Einkaufs-/Fakturawert",
              "Wiederbeschaffungswert",
              "sonstige",
            ],
            source: "VWERT",
            hint: "Bewertungsgrundlage für den Fahrzeuginhalt.",
          },
          {
            id: "WV-V07",
            key: "wv_v07",
            label: "Selbstbehalt Werkverkehr",
            feld: "Selbstbehalt",
            type: "combo",
            parts: [
              { key: "art", label: "Art", type: "select", options: [
                "fester Selbstbehalt in EUR",
                "prozentualer Selbstbehalt in %",
                "kombiniert",
                "kein Selbstbehalt",
              ] },
              { key: "eur", label: "Betrag (EUR)", type: "eur" },
              { key: "prozent", label: "Anteil (%)", type: "percent" },
            ],
            source: "SBFIX / SBPROZ",
            hint: "Standard-Selbstbehalt.",
          },
          {
            id: "WV-V04",
            key: "wv_v04",
            label: "Geltungsbereich Werkverkehr",
            feld: "Geltungsbereich",
            type: "single",
            options: [
              "Deutschland",
              "geografisches Europa",
              "weltweit",
              "individuell",
            ],
            source: "GELTBER",
            hint: "Geltungsbereich speziell für den Werkverkehr.",
          },
          {
            id: "WV-V03",
            key: "wv_v03",
            label: "Deckungszeitraum Werkverkehr",
            feld: "Deckungszeitraum",
            type: "single",
            options: [
              "nur während des Transports",
              "24/7 im Fahrzeug",
              "individuelle zeitliche Regelung",
            ],
            source: "TR-V37",
            hint: "Zentrale Abgrenzung bei Fahrzeuginhaltsdeckung.",
          },
        ],
      },
      {
        letter: "B",
        key: "werkverkehr_b",
        title: "Deckungsdetails",
        fields: [
          {
            id: "WV-V01",
            key: "wv_v01",
            label: "Versicherte Fahrzeuge",
            feld: "Versicherte Fahrzeuge / Fahrzeuggruppen",
            type: "multi",
            options: [
              "PKW",
              "Lieferwagen / LFW",
              "LKW",
              "Anhänger",
              "sonstige eigene Fahrzeuge",
            ],
            source: "Kfz-Verknüpfung / SKZ 331",
            hint: "Fahrzeuge möglichst aus dem Kfz-Bestand verknüpfen.",
          },
          {
            id: "WV-V02",
            key: "wv_v02",
            label: "Versicherter Fahrzeuginhalt",
            feld: "Versicherte Sachen im Fahrzeug",
            type: "multi",
            options: [
              "eigene Waren / Produkte",
              "Werkzeuge / Geräte",
              "Ersatzteile / Material",
              "Muster / Modelle",
              "sonstige Sachen",
            ],
            source: "fachliche Ergänzung",
            hint: "Nur die im Werkverkehr tatsächlich versicherten Sachen.",
          },
          {
            id: "WV-V08",
            key: "wv_v08",
            label: "Nachtzeit / Abstellen",
            feld: "Nachtzeit-/Abstellregelung",
            type: "subtable",
            subtable: {
              rowLabel: "Abstellart",
              rows: [
                "eigenes Betriebsgelände",
                "Garage/Halle",
                "bewachter Parkplatz",
                "unbewachter Parkplatz",
                "öffentlicher Verkehrsraum",
                "Hotelparkplatz",
                "Kunden-/Baustellengelände",
                "sonstige",
              ],
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "versichert",
                  "eingeschränkt",
                  "ausgeschlossen",
                  "nur unter Voraussetzungen",
                ], width: "200px" },
                { key: "voraussetzung", label: "Voraussetzung", type: "text" },
              ],
            },
            source: "TR-V28",
            hint: "Nur relevant, wenn beladene Fahrzeuge abgestellt werden.",
          },
        ],
      },
      {
        letter: "C",
        key: "werkverkehr_c",
        title: "Bedingungen / Klauseln / Einschränkungen",
        fields: [
          {
            id: "WV-V11",
            key: "wv_v11",
            label: "Klauseln Werkverkehr",
            feld: "Klauseln / Sondervereinbarungen",
            type: "subtable",
            subtable: {
              rowLabel: "Klausel",
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "eingeschlossen",
                  "ausgeschlossen",
                  "abweichend vereinbart",
                ], width: "200px" },
                { key: "limit", label: "Limit (EUR)", type: "eur", width: "150px" },
                { key: "sb", label: "Selbstbehalt", type: "text", width: "140px" },
                { key: "bemerkung", label: "Bemerkung", type: "text" },
              ],
            },
            source: "TR-V33",
            hint: "Nur werkverkehrsbezogene Klauseln.",
          },
        ],
      },
      {
        letter: "D",
        key: "werkverkehr_d",
        title: "Informationen aus der Analyse",
        description:
          "Werte aus der Transport-Analyse. Sie werden hier nur gezeigt — geändert werden sie in der Analyse.",
        fields: [
          {
            id: "WV-A01",
            key: "wv_a01",
            label: "Eigene Fahrzeuge relevant",
            feld: "Eigene Fahrzeuge für Transporte",
            type: "readout",
            source: "Allg. Analyse 178",
            hint: "Hinweis, ob eigene Fahrzeuge bei Versendungen eingesetzt werden.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "WV-A02",
            key: "wv_a02",
            label: "Nachtabstellung",
            feld: "Nachtabstellung beladener Fahrzeuge",
            type: "readout",
            source: "TR-D34",
            hint: "Wichtig für Nachtzeit-/Diebstahlklauseln.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "WV-A03",
            key: "wv_a03",
            label: "Maximalwert Nachtabstellung",
            feld: "Maximalwert bei Nachtabstellung",
            type: "readout",
            source: "TR-D36",
            hint: "Mit dem vertraglichen Maximum vergleichen.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "WV-A04",
            key: "wv_a04",
            label: "24/7-Bedarf eigene Fahrzeuge",
            feld: "24/7-Bedarf",
            type: "readout",
            source: "TR-D64",
            hint: "Zeigt, ob Inhalt auch außerhalb der reinen Transportdauer gedeckt sein soll.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
        ],
      },
    ],
  },
  // ── 4. Frachtführerhaftpflicht ───────────────────────────────────
  {
    key: "frachtfuehrer",
    nr: 4,
    title: "Frachtführerhaftpflicht",
    shortTitle: "Frachtführer",
    color: "purple",
    intro:
      "Versicherte Rolle und Haftungssummen zuerst, Tätigkeitsdetails danach, Haftungsbedingungen und Klauseln vor den Analyseinformationen.",
    uebersicht: [
      { label: "Deckungssumme", fieldKey: "ff_v05" },
      { label: "Versicherte Rolle", fieldKey: "ff_v01" },
      { label: "Selbstbehalt", fieldKey: "ff_v07" },
      { label: "Haftungsgrundlage", fieldKey: "ff_v02" },
    ],
    blocks: [
      {
        letter: "A",
        key: "frachtfuehrer_a",
        title: "Wichtigste Vertragsdaten",
        fields: [
          {
            id: "FF-V01",
            key: "ff_v01",
            label: "Versicherte Rolle",
            feld: "Versicherte Rolle / Tätigkeit",
            type: "multi",
            options: [
              "Frachtführer",
              "Spediteur",
              "Lagerhalter",
              "Logistikdienstleister",
              "Vermittler",
              "Kurier-/Paketdienst",
              "sonstige",
            ],
            source: "SKZ 430 / 450 / 460",
            hint: "Welche Haftungsrolle ist vom Vertrag erfasst?",
          },
          {
            id: "FF-V05",
            key: "ff_v05",
            label: "Deckungssumme",
            feld: "Deckungssumme / Haftungshöchstsumme",
            type: "subtable",
            subtable: {
              rowLabel: "Bezug",
              rows: [
                "je Schadenfall",
                "je Schadenereignis",
                "kg-/sendungsbezogene Haftungsgrenze",
              ],
              addable: true,
              columns: [
                { key: "betrag", label: "Betrag (EUR)", type: "eur", width: "180px" },
                { key: "bemerkung", label: "Bemerkung", type: "text" },
              ],
            },
            source: "fachliche Ergänzung",
            hint: "Wichtigster Summenwert der Haftpflichtdeckung.",
          },
          {
            id: "FF-V06",
            key: "ff_v06",
            label: "Jahreshöchstleistung",
            type: "eurSelect",
            parts: [
              { key: "betrag", label: "Betrag (EUR)", type: "eur" },
              { key: "maximierung", label: "Maximierung", type: "select", options: [
                "einfache Jahreshöchstleistung",
                "zweifache Maximierung",
                "dreifache Maximierung",
                "unbegrenzt / keine Maximierung",
              ] },
            ],
            source: "fachliche Ergänzung",
            hint: "Nur wenn vertraglich begrenzt.",
          },
          {
            id: "FF-V07",
            key: "ff_v07",
            label: "Selbstbehalt Frachtführerhaftpflicht",
            feld: "Selbstbehalt",
            type: "combo",
            parts: [
              { key: "art", label: "Art", type: "select", options: [
                "fester Selbstbehalt in EUR",
                "prozentualer Selbstbehalt in %",
                "kombiniert",
                "kein Selbstbehalt",
              ] },
              { key: "eur", label: "Betrag (EUR)", type: "eur" },
              { key: "prozent", label: "Anteil (%)", type: "percent" },
            ],
            source: "SBFIX / SBPROZ",
            hint: "Standard-Selbstbehalt.",
          },
        ],
      },
      {
        letter: "B",
        key: "frachtfuehrer_b",
        title: "Deckungsdetails",
        fields: [
          {
            id: "FF-V03",
            key: "ff_v03",
            label: "Versicherte Tätigkeiten",
            feld: "Versicherte Tätigkeiten / Nebenleistungen",
            type: "multi",
            options: [
              "Transport",
              "Be-/Entladung",
              "Umschlag",
              "Kommissionierung",
              "Verpackung",
              "Lagerung",
              "Organisation der Transportkette",
              "sonstige",
            ],
            source: "Allg. 293 / Vertrag",
            hint: "Nur tatsächlich mitversicherte logistische Leistungen.",
          },
          {
            id: "FF-V08",
            key: "ff_v08",
            label: "Subunternehmer mitversichert",
            feld: "Subunternehmer / eingesetzte Frachtführer",
            type: "single",
            options: [
              "mitversichert",
              "nur unter Voraussetzungen",
              "nicht mitversichert",
              "individuelle Regelung",
            ],
            source: "fachliche Ergänzung",
            hint: "Relevant, wenn Transporte weitervergeben werden.",
          },
          {
            id: "FF-V09",
            key: "ff_v09",
            label: "Lagerhalterhaftung mitversichert",
            feld: "Lagerhalterrisiko",
            type: "single",
            options: [
              "nein",
              "ja",
              "nur separat / eigener Vertrag",
            ],
            source: "Allg. 190 / Vertrag",
            hint: "Lagerhalterrisiken klar von der reinen Frachtführerhaftung abgrenzen.",
          },
        ],
      },
      {
        letter: "C",
        key: "frachtfuehrer_c",
        title: "Bedingungen / Klauseln / Einschränkungen",
        fields: [
          {
            id: "FF-V02",
            key: "ff_v02",
            label: "Haftungsgrundlage",
            feld: "Haftungs-/Bedingungsgrundlage",
            type: "multi",
            options: [
              "HGB",
              "CMR",
              "ADSp / vereinbarte Speditionsbedingungen",
              "individuelle Verkehrsverträge",
              "sonstige",
            ],
            source: "fachliche Ergänzung",
            hint: "Zentrale Grundlage für die Haftungsprüfung.",
          },
          {
            id: "FF-V10",
            key: "ff_v10",
            label: "Klauseln Frachtführerhaftpflicht",
            feld: "Klauseln / Sondervereinbarungen",
            type: "subtable",
            subtable: {
              rowLabel: "Klausel",
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "eingeschlossen",
                  "ausgeschlossen",
                  "abweichend vereinbart",
                ], width: "200px" },
                { key: "limit", label: "Limit (EUR)", type: "eur", width: "150px" },
                { key: "sb", label: "Selbstbehalt", type: "text", width: "140px" },
                { key: "bemerkung", label: "Bemerkung", type: "text" },
              ],
            },
            source: "TR-V33",
            hint: "Nur haftpflichtbezogene Klauseln.",
          },
        ],
      },
      {
        letter: "D",
        key: "frachtfuehrer_d",
        title: "Informationen aus der Analyse",
        description:
          "Werte aus der Transport-Analyse. Sie werden hier nur gezeigt — geändert werden sie in der Analyse.",
        fields: [
          {
            id: "FF-A01",
            key: "ff_a01",
            label: "Transporte für Dritte",
            type: "readout",
            source: "Allg. Analyse 188",
            hint: "Zeigt, ob überhaupt ein Frachtführer-/Verkehrshaftungsrisiko besteht.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "FF-A02",
            key: "ff_a02",
            label: "Rolle Transport / Logistik",
            feld: "Rolle im Transport / in der Logistik",
            type: "readout",
            source: "Allg. Analyse 292",
            hint: "Frachtführer, Spediteur, Lagerhalter etc. aus der allgemeinen Analyse.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "FF-A03",
            key: "ff_a03",
            label: "Logistische Nebenleistungen",
            type: "readout",
            source: "Allg. Analyse 293",
            hint: "Hinweis auf zusätzliche versicherte Tätigkeiten.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "FF-A04",
            key: "ff_a04",
            label: "Verantwortung Transportkette",
            feld: "Verantwortung für die Transportkette",
            type: "readout",
            source: "Allg. Analyse 294",
            hint: "Wichtig für den Umfang der Haftung.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
        ],
      },
    ],
  },
  // ── 5. Messeversicherung ─────────────────────────────────────────
  {
    key: "messe",
    nr: 5,
    title: "Messeversicherung",
    shortTitle: "Messe",
    color: "pillarOrange",
    intro:
      "Versicherungssummen, Selbstbehalt und Geltungsbereich zuerst, Veranstaltungsdetails danach, Klauseln vor den Analyseinformationen.",
    uebersicht: [
      { label: "Versicherungssumme", fieldKey: "me_v03" },
      { label: "Geltungsbereich", fieldKey: "me_v02" },
      { label: "Selbstbehalt", fieldKey: "me_v08" },
      { label: "Maximum je Exponat", fieldKey: "me_v04" },
    ],
    blocks: [
      {
        letter: "A",
        key: "messe_a",
        title: "Wichtigste Vertragsdaten",
        fields: [
          {
            id: "ME-V03",
            key: "me_v03",
            label: "Versicherungssumme Messe",
            feld: "Versicherungssumme je Veranstaltung",
            type: "eur",
            placeholder: "0,00",
            source: "ME-V02",
            hint: "Zentrale Versicherungssumme.",
          },
          {
            id: "ME-V04",
            key: "me_v04",
            label: "Maximum je Exponat",
            type: "eur",
            placeholder: "0,00",
            source: "ME-V02",
            hint: "Mit dem höchsten Einzelwert aus der Analyse vergleichen.",
          },
          {
            id: "ME-V08",
            key: "me_v08",
            label: "Selbstbehalt Messe",
            feld: "Selbstbehalt",
            type: "combo",
            parts: [
              { key: "art", label: "Art", type: "select", options: [
                "fester Selbstbehalt in EUR",
                "prozentualer Selbstbehalt in %",
                "kein Selbstbehalt",
              ] },
              { key: "eur", label: "Betrag (EUR)", type: "eur" },
              { key: "prozent", label: "Anteil (%)", type: "percent" },
            ],
            source: "ME-V02 / TR-V27",
            hint: "Messespezifischen Selbstbehalt separat führen.",
          },
          {
            id: "ME-V02",
            key: "me_v02",
            label: "Geltungsbereich Messe",
            feld: "Geltungsbereich",
            type: "single",
            options: [
              "Deutschland",
              "geografisches Europa",
              "weltweit",
              "individuell",
            ],
            source: "ME-V01",
            hint: "Räumlicher Geltungsbereich der Messeversicherung.",
          },
        ],
      },
      {
        letter: "B",
        key: "messe_b",
        title: "Deckungsdetails",
        fields: [
          {
            id: "ME-V01",
            key: "me_v01",
            label: "Versicherte Veranstaltungen",
            type: "subtable",
            subtable: {
              rowLabel: "Veranstaltung",
              addable: true,
              columns: [
                { key: "ort", label: "Ort", type: "text", width: "180px" },
                { key: "land", label: "Land", type: "text", width: "140px" },
                { key: "zeitraum", label: "Zeitraum", type: "text", width: "170px" },
                { key: "bemerkung", label: "Bemerkung", type: "text" },
              ],
            },
            source: "ME-V01",
            hint: "Welche Veranstaltungen sind vom Vertrag erfasst?",
          },
          {
            id: "ME-V05",
            key: "me_v05",
            label: "Deckungsphasen Messe",
            feld: "Deckungsphasen",
            type: "subtable",
            subtable: {
              rowLabel: "Phase",
              rows: [
                "Hintransport",
                "Aufenthalt",
                "Auf-/Abbau",
                "Zwischenlagerung",
                "Rücktransport",
              ],
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "versichert",
                  "eingeschränkt",
                  "ausgeschlossen",
                ], width: "170px" },
                { key: "bemerkung", label: "Bemerkung", type: "text" },
              ],
            },
            source: "ME-V03",
            hint: "Zeigt, welche Phasen tatsächlich versichert sind.",
          },
          {
            id: "ME-V06",
            key: "me_v06",
            label: "Nagel-zu-Nagel",
            feld: "Nagel-zu-Nagel-Deckung",
            type: "single",
            options: [
              "nein",
              "ja",
              "individuelle Regelung",
            ],
            source: "ME-V04",
            hint: "Wichtig bei durchgehender Deckung.",
          },
          {
            id: "ME-V07",
            key: "me_v07",
            label: "Fremde Exponate",
            feld: "Fremde / geliehene Exponate",
            type: "single",
            options: [
              "versichert",
              "eingeschränkt",
              "ausgeschlossen",
            ],
            source: "ME-V04",
            hint: "Bei Leihgaben besonders wichtig.",
          },
        ],
      },
      {
        letter: "C",
        key: "messe_c",
        title: "Bedingungen / Klauseln / Einschränkungen",
        fields: [
          {
            id: "ME-V09",
            key: "me_v09",
            label: "Klauseln Messe",
            feld: "Klauseln / Sondervereinbarungen",
            type: "subtable",
            subtable: {
              rowLabel: "Klausel",
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "eingeschlossen",
                  "ausgeschlossen",
                  "abweichend vereinbart",
                ], width: "200px" },
                { key: "limit", label: "Limit (EUR)", type: "eur", width: "150px" },
                { key: "sb", label: "Selbstbehalt", type: "text", width: "140px" },
                { key: "bemerkung", label: "Bemerkung", type: "text" },
              ],
            },
            source: "ME-V04 / TR-V33",
            hint: "Nur messebezogene Klauseln.",
          },
        ],
      },
      {
        letter: "D",
        key: "messe_d",
        title: "Informationen aus der Analyse",
        description:
          "Werte aus der Transport-Analyse. Sie werden hier nur gezeigt — geändert werden sie in der Analyse.",
        fields: [
          {
            id: "ME-A01",
            key: "me_a01",
            label: "Messeteilnahme",
            type: "readout",
            source: "Allg. Analyse 191",
            hint: "Zeigt, ob Messe-/Ausstellungsrisiken bestehen.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "ME-A02",
            key: "me_a02",
            label: "Höchster Veranstaltungswert",
            feld: "Höchster Messe-/Ausstellungswert",
            type: "readout",
            source: "ME-D06",
            hint: "Mit der vertraglichen Versicherungssumme vergleichen.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "ME-A03",
            key: "me_a03",
            label: "Höchster Einzelwert Exponat",
            type: "readout",
            source: "ME-D07",
            hint: "Wichtig für Einzelstück-/Exponatlimits.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
        ],
      },
    ],
  },
  // ── 6. Reisegepäckversicherung ───────────────────────────────────
  {
    key: "reisegepaeck",
    nr: 6,
    title: "Reisegepäckversicherung",
    shortTitle: "Reisegepäck",
    color: "black",
    intro:
      "Summen, Selbstbehalt und Geltungsbereich zuerst, versicherte Personen und Sachen danach, Klauseln vor den Analyseinformationen.",
    uebersicht: [
      { label: "Summe je Person", fieldKey: "rg_v05" },
      { label: "Geltungsbereich", fieldKey: "rg_v02" },
      { label: "Selbstbehalt", fieldKey: "rg_v07" },
      { label: "Summe je Reise", fieldKey: "rg_v06" },
    ],
    blocks: [
      {
        letter: "A",
        key: "reisegepaeck_a",
        title: "Wichtigste Vertragsdaten",
        fields: [
          {
            id: "RG-V05",
            key: "rg_v05",
            label: "Versicherungssumme je Person",
            type: "eur",
            placeholder: "0,00",
            source: "RG-V03",
            hint: "Zentrale Summe je versicherter Person.",
          },
          {
            id: "RG-V06",
            key: "rg_v06",
            label: "Versicherungssumme je Reise",
            feld: "Versicherungssumme je Reise / Ereignis",
            type: "eur",
            placeholder: "0,00",
            source: "RG-V03",
            hint: "Zusätzlich zur Personensumme, soweit vorgesehen.",
          },
          {
            id: "RG-V07",
            key: "rg_v07",
            label: "Selbstbehalt Reisegepäck",
            feld: "Selbstbehalt",
            type: "combo",
            parts: [
              { key: "art", label: "Art", type: "select", options: [
                "fester Selbstbehalt in EUR",
                "prozentualer Selbstbehalt in %",
                "kein Selbstbehalt",
              ] },
              { key: "eur", label: "Betrag (EUR)", type: "eur" },
              { key: "prozent", label: "Anteil (%)", type: "percent" },
            ],
            source: "RG-V03",
            hint: "Reisegepäckspezifischen Selbstbehalt separat führen.",
          },
          {
            id: "RG-V02",
            key: "rg_v02",
            label: "Geltungsbereich Reisegepäck",
            feld: "Geltungsbereich",
            type: "single",
            options: [
              "Deutschland",
              "geografisches Europa",
              "weltweit",
              "individuell",
            ],
            source: "RG-V01",
            hint: "Räumlicher Geltungsbereich.",
          },
        ],
      },
      {
        letter: "B",
        key: "reisegepaeck_b",
        title: "Deckungsdetails",
        fields: [
          {
            id: "RG-V01",
            key: "rg_v01",
            label: "Versicherte Personen",
            type: "multi",
            options: [
              "alle Mitarbeiter",
              "benannte Mitarbeiter / Personengruppe",
              "Geschäftsleitung",
              "sonstige Personengruppe",
            ],
            source: "RG-V01",
            hint: "Welche Personen sind vom Vertrag erfasst?",
          },
          {
            id: "RG-V03",
            key: "rg_v03",
            label: "Versicherte Sachen Reisegepäck",
            feld: "Versicherte Sachen",
            type: "multi",
            options: [
              "persönliches Reisegepäck",
              "firmeneigene Sachen",
              "Muster / Waren",
              "Elektronik / Laptops",
              "Wertsachen",
              "Dokumente",
              "Werkzeuge / Geräte",
              "sonstige",
            ],
            source: "RG-V02",
            hint: "Nur tatsächlich versicherte Sachen.",
          },
          {
            id: "RG-V04",
            key: "rg_v04",
            label: "Sublimits Reisegepäck",
            feld: "Sublimits",
            type: "subtable",
            subtable: {
              rowLabel: "Sachenart",
              rows: [
                "persönliches Reisegepäck",
                "firmeneigene Sachen",
                "Muster / Waren",
                "Elektronik / Laptops",
                "Wertsachen",
                "Dokumente",
                "Werkzeuge / Geräte",
                "sonstige",
              ],
              addable: true,
              columns: [
                { key: "sublimit", label: "Sublimit (EUR)", type: "eur", width: "170px" },
                { key: "einschraenkung", label: "Einschränkung / Ausschluss", type: "text" },
              ],
            },
            source: "RG-V02",
            hint: "Besonders für Elektronik, Wertsachen, Muster/Waren relevant.",
          },
        ],
      },
      {
        letter: "C",
        key: "reisegepaeck_c",
        title: "Bedingungen / Klauseln / Einschränkungen",
        fields: [
          {
            id: "RG-V08",
            key: "rg_v08",
            label: "Klauseln Reisegepäck",
            feld: "Klauseln / Sondervereinbarungen",
            type: "subtable",
            subtable: {
              rowLabel: "Klausel",
              addable: true,
              columns: [
                { key: "status", label: "Status", type: "select", options: [
                  "eingeschlossen",
                  "ausgeschlossen",
                  "abweichend vereinbart",
                ], width: "200px" },
                { key: "limit", label: "Limit (EUR)", type: "eur", width: "150px" },
                { key: "sb", label: "Selbstbehalt", type: "text", width: "140px" },
                { key: "bemerkung", label: "Bemerkung", type: "text" },
              ],
            },
            source: "RG-V02 / TR-V33",
            hint: "Nur reisegepäckbezogene Klauseln.",
          },
        ],
      },
      {
        letter: "D",
        key: "reisegepaeck_d",
        title: "Informationen aus der Analyse",
        description:
          "Werte aus der Transport-Analyse. Sie werden hier nur gezeigt — geändert werden sie in der Analyse.",
        fields: [
          {
            id: "RG-A01",
            key: "rg_a01",
            label: "Reisegepäck relevant",
            feld: "Sachen auf Geschäftsreisen",
            type: "readout",
            source: "Allg. Analyse 324",
            hint: "Zeigt, ob Reisegepäckdeckung benötigt wird.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
          {
            id: "RG-A02",
            key: "rg_a02",
            label: "Höchstwert Reisegepäck",
            feld: "Höchstwert je Person / Reise",
            type: "readout",
            source: "RG-D04",
            hint: "Mit der vertraglichen Summe je Person/Reise vergleichen.",
            check: "Details bei Bedarf in der Analyse öffnen.",
          },
        ],
      },
    ],
  },
];

/** Alle Vertragsarten nach Schlüssel. */
export const VERTRAGSART_BY_KEY = Object.fromEntries(
  TRANSPORT_VERTRAGSARTEN.map((v) => [v.key, v]),
) as Record<string, TrVertragsart>;

/** Feld-Nachschlag über alle Vertragsarten — für Übersichtskarten und Abgleich. */
export const FIELD_BY_KEY = Object.fromEntries(
  TRANSPORT_VERTRAGSARTEN.flatMap((v) =>
    v.blocks.flatMap((b) => b.fields.map((f) => [f.key, f] as const)),
  ),
);
